---
title: Konfigurace rozsahu
sidebar_position: 5
description: Konfigurace rozsahu
---

# Konfigurace a přizpůsobení rozsahu

## Přehled

Každý rozsah v aplikaci AM App má přizpůsobitelná nastavení, která určují, které funkce, vrstvy a výchozí hodnoty jsou k dispozici. Konfigurace je uložena per-rozsah ve službě správy. Integrovaný **Editor konfigurace** (Config Editor) umožňuje administrátorům s rolí **SUPER-SYSADMIN** upravovat konfiguraci přímo v aplikaci. Koncoví uživatelé bez této role Editor konfigurace nevidí.

#### Proč je konfigurace rozsahu důležitá

- Různé zemědělské regiony mají různé potřeby monitorování
- Rozsahy lze přizpůsobit konkrétním případům užití, plodinám, sezónám nebo pravidlům shody
- Konfigurace určuje, co vidíte a jak pracujete s daty

**Důležité**: Jako koncový uživatel nemůžete konfiguraci rozsahu přímo měnit. Konfiguraci spravují administrátoři. Pochopení toho, co je konfigurovatelné, však pomáhá pochopit, proč se rozsahy chovají různě.

## Úprava konfigurace rozsahu

**Editor konfigurace** zobrazuje uložený JSON rozsahu a poskytuje:

- Editor kódu JSON se zvýrazněním syntaxe
- Průběžnou validaci vůči schématu konfigurace — neplatné úpravy blokují uložení, takže chyby schématu jsou zachyceny dříve, než se dostanou k uživatelům
- Upozornění při neuložených změnách
- Tlačítko **Uložit**, které zapíše aktualizovanou konfiguraci zpět do rozsahu okamžitě

Přístup: otevřete postranní panel a vyberte **Editor konfigurace**.

## Konfigurovatelné prvky per rozsah

Pro každý rozsah lze přizpůsobit následující prvky:

---

### Výchozí hodnoty časového období

**Co ovládá**:

- Výchozí datum začátku a konce při otevření rozsahu
- Obvykle nastaveno na aktuální vegetační sezónu nebo sledované období

**Dopad na uživatele**:

- Při prvním výběru rozsahu je časové období přednastaveno
- Tyto výchozí hodnoty lze přepsat pomocí panelu **Možnosti**
- Výchozí hodnoty zajišťují, že pracujete s relevantními daty

**Příklad**:

```json
{
  "options": {
    "timerange": {
      "start": "2023-01-01",
      "end": "2023-12-31"
    }
  }
}
```

---

### Nastavení mapy

**Co ovládá**:

1. **Dostupné základní vrstvy** (`map.baseLayers`):
   - Které dlaždičkové vrstvy jsou dostupné v menu **Vrstvy**
   - Jsou podporovány tři typy zdrojů: `WMS`, `WMTS` a `XYZ` — nastavují se pomocí pole `type` u každé položky
   - Všechny tři typy sdílejí společná pole `id` (jedinečný identifikátor) a `label` (zobrazovaný název v menu **Vrstvy**)
   - Pole specifická pro jednotlivé typy:
     - **WMS**: `params.LAYERS` (povinné — název vrstvy jako řetězec nebo pole); volitelně `url` (jeden endpoint) nebo `urls` (pole endpointů), `serverType`, `projection`, `tileGrid`
     - **WMTS**: `layer`, `style`, `matrixSet`, `tileGrid` (s `origin`, `tileSize`, `resolutions`, `matrixIds`); volitelně `url`, `format`, `projection`
     - **XYZ**: volitelně `url` (šablona URL dlaždic s zástupnými znaky `{z}`, `{x}`, `{y}`) nebo `urls` (pole šablon), `maxZoom`, `minZoom`, `projection`
   - Všechny typy přijímají volitelné `attributions` (řetězec zobrazovaný v ovládacím prvku přiřazení mapy)

2. **Prahová hodnota přiblížení pro zákresy JŽ** (`map.foiZoomThreshold`):
   - Úroveň přiblížení, při které se zákresy JŽ stávají viditelnými na mapě
   - Předchází nepřehlednosti při oddálení
   - Typická hodnota: úroveň přiblížení 12–14

3. **Prahová hodnota přiblížení pro popisky per typ zákresu JŽ** (`map.labelZoomThresholdPerFoiType`):
   - Úroveň přiblížení per typ zákresu JŽ, při které se popisky zákresu JŽ zobrazí na mapě
   - Umožňuje přesné řízení popisků, pokud rozsah obsahuje více typů zákresu JŽ s různou hustotou

4. **Systém měrných jednotek** (`map.measurement`):
   - Ovládá **pouze jednotky plochy**: metrické (m²/km² automaticky škálované nebo hektary) nebo imperiální (akry)
   - Vzdálenost je vždy zobrazena v metrech nebo kilometrech bez ohledu na toto nastavení

**Dopad na uživatele**:

- Různé rozsahy nabízejí různé základní vrstvy
- Některé rozsahy mohou používat imperiální jednotky plochy (např. projekty v USA)
- Viditelnost zákresu JŽ a zobrazení popisků se mění v závislosti na úrovni přiblížení

**Příklad**:

```json
{
  "map": {
    "baseLayers": [
      {
        "id": "world_imagery",
        "label": "World Imagery",
        "type": "XYZ",
        "url": "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        "attributions": "Tiles © ArcGIS",
        "maxZoom": 19
      },
      {
        "id": "my_wms_layer",
        "label": "National Orthophoto (WMS)",
        "type": "WMS",
        "url": "https://example.com/ows",
        "params": {
          "LAYERS": "orthophoto_2023",
          "VERSION": "1.3.0"
        },
        "attributions": "© National Mapping Agency"
      },
      {
        "id": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "url": "https://data.geopf.fr/wmts",
        "type": "WMTS",
        "label": "Aerial imagery 50 cm",
        "layer": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "style": "normal",
        "format": "image/jpeg",
        "tileGrid": {
          "origin": [-20037508.342789244, 20037508.342789244],
          "tileSize": 256,
          "matrixIds": [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
            "10",
            "11",
            "12",
            "13",
            "14",
            "15",
            "16",
            "17",
            "18"
          ],
          "resolutions": [
            156543.03392804097, 78271.51696402048, 39135.75848201024, 19567.87924100512, 9783.93962050256,
            4891.96981025128, 2445.98490512564, 1222.99245256282, 611.49622628141, 305.748113140705,
            152.8740565703525, 76.43702828517625, 38.21851414258813, 19.109257071294063, 9.554628535647032,
            4.777314267823516, 2.388657133911758, 1.194328566955879, 0.5971642834779395
          ]
        },
        "matrixSet": "PM_6_18",
        "projection": "EPSG:3857",
        "attributions": "© IGN"
      }
    ],
    "foiZoomThreshold": 14,
    "labelZoomThresholdPerFoiType": {
      "PARCEL": 13,
      "HOLDING": 10
    },
    "measurement": {
      "system": "metric",
      "areaUnit": "sqm-km2-auto"
    }
  }
}
```

---

### Mapové projekce

**Co ovládá**:

Vlastní souřadnicové referenční systémy (CRS — Coordinate Reference System), které mapa pro daný rozsah podporuje. Ve výchozím nastavení aplikace používá Web Mercator (EPSG:3857). Pokud jsou data rozsahu uložena v národní projekci — například česká S-JTSK (EPSG:5514) nebo francouzská Lambert-93 (EPSG:2154) — musí být odpovídající CRS zaregistrován zde, aby byly geometrie zobrazovány a měřeny správně.

Každá položka v poli `projections` obsahuje:

| Pole         | Povinné | Popis                                                                           |
| ------------ | ------- | ------------------------------------------------------------------------------- |
| `crs`        | Ano     | Identifikátor CRS, např. `"EPSG:5514"`                                          |
| `definition` | Ano     | Definice Proj4 pro daný CRS                                                     |
| `extent`     | Ne      | Ohraničující rámeček v nativních jednotkách CRS: `minX`, `minY`, `maxX`, `maxY` |

**Dopad na uživatele**:

- Umožňuje správné vykreslení geometrií zákresu JŽ uložených v národní projekci
- Umožňuje přesné měření ploch a vzdáleností v nativních jednotkách rozsahu
- Bez zaregistrované odpovídající projekce mohou geometrie na mapě vypadat zkreslené nebo posunuté

**Příklad**:

```json
{
  "projections": [
    {
      "crs": "EPSG:5514",
      "definition": "+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 +alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=589,76,480,0,0,0,0 +units=m +no_defs +type=crs",
      "extent": {
        "minX": -951499.37,
        "minY": -1250000.0,
        "maxX": -159365.31,
        "maxY": -915744.49
      }
    }
  ]
}
```

---

### Výchozí nastavení mapové vrstvy per typ zákresu JŽ

**Co ovládá**:

Každý typ zákresu JŽ může mít vlastní výchozí nastavení zobrazení na mapě prostřednictvím `map.layerOptions`. Klíčem je kód typu zákresu JŽ; každá položka ovládá:

| Vlastnost          | Typ                                   | Popis                                                               |
| ------------------ | ------------------------------------- | ------------------------------------------------------------------- |
| `visible`          | boolean                               | Zda je tento typ zákresu JŽ na mapě ve výchozím nastavení viditelný |
| `border`           | boolean                               | Zda jsou kreslena ohraničení zákresu JŽ                             |
| `fill`             | boolean                               | Zda jsou polygony zákresu JŽ vyplněny                               |
| `opacity`          | číslo (0–100)                         | Procentuální průhlednost výplně                                     |
| `labels`           | pole řetězců                          | Klíče atributů zobrazených jako popisky na mapě                     |
| `colorBy`          | `fixedColor` / `attribute` / `marker` | Způsob obarvení polygonů zákresu JŽ                                 |
| `fixedColor`       | řetězec (nullable)                    | Hexadecimální barva použitá při `colorBy` rovném `fixedColor`       |
| `colorByAttribute` | řetězec (nullable)                    | Klíč atributu použitý při `colorBy` rovném `attribute`              |
| `colorByMarker`    | řetězec (nullable)                    | Kód typu indikátoru použitý při `colorBy` rovném `marker`           |

**Dopad na uživatele**:

- Po otevření rozsahu se typy zákresu JŽ zobrazí na mapě s barvami, výplněmi a popisky nastavenými administrátorem
- Tato výchozí nastavení lze přepsat v panelu možností vizualizace zákresu JŽ — vaše osobní nastavení mají přednost
- Pokud má rozsah více typů zákresu JŽ, může mít každý z nich ve výchozím nastavení odlišný vizuální styl

**Příklad**:

```json
{
  "map": {
    "layerOptions": {
      "PARCEL": {
        "visible": true,
        "border": true,
        "fill": true,
        "opacity": 60,
        "labels": ["ref_id"],
        "colorBy": "marker",
        "colorByMarker": "ARPS_HOMOGENEITY_MARKER"
      },
      "HOLDING": {
        "visible": true,
        "border": true,
        "fill": false,
        "colorBy": "fixedColor",
        "fixedColor": "#FFD700"
      }
    }
  }
}
```

---

### Integrace Sentinel Hub

**Co ovládá**:

- **ID instance**: Identifikátor pro přístup k vrstvám Sentinel Hub
- **Dostupné vrstvy pozorování**: Které vizualizace jsou nabízeny
  - Příklady: NDVI, True Color, False Color, EVI, LAI atd.
  - Každý rozsah může mít jedinečnou sadu vrstev definovaných v konfiguracích Planet Insights Platform

**Dopad na uživatele**:

- Vrstvy pozorování, které vidíte v panelu **Možnosti**, se liší podle rozsahu
- Některé rozsahy mají mnoho vrstev, jiné jen několik
- Dostupnost vrstev závisí na potřebách projektu a konfiguraci Sentinel Hub

**Příklad**:

```json
{
  "sentinelHub": {
    "shInstanceId": "81b306fe-0435-44bd-9f1f-d516999a8e97"
  }
}
```

---

### Konfigurace signálů

Pro každý typ signálu může konfigurace rozsahu definovat:

**Co ovládá**:

1. **Které typy signálů jsou dostupné**: Rozsah určuje, které signály jsou vypočítávány
2. **Výchozí viditelnost ve vizualizaci signálů a indikátorů**: Zda je signál ve výchozím nastavení zobrazen
3. **Výchozí viditelnost v nápovědě**: Zda se signál ve výchozím nastavení zobrazuje v nápovědách
4. **Barevná schémata**: Barvy pro světlý a tmavý režim pro každý signál (jeden barevný řetězec nebo pole barev pro gradientní signály)
5. **Přepsání platných typů signálů** (`options.validSignalTypeOverrides`): Přemapovává, který typ signálu se používá k určení, zda je pozorování platné. Každá položka je klíčována kódem typu signálu; hodnotou je náhradní kód typu signálu, který se použije místo něj, nebo `null` pro potlačení filtrování platnosti pro daný typ.

**Dopad na uživatele**:

- Widget **Vizualizace signálů a indikátorů** zobrazí signály definované pro váš rozsah
- Některé signály mohou být ve výchozím nastavení skryty (zapněte je v legendě)
- Barvy signálů jsou v rámci rozsahu konzistentní

**Příklad**:

```json
{
  "signals": {
    "DEMO_FR23_S2_NDVI": {
      "sigMaVisShow": true,
      "sigMaVisTooltip": true,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      }
    }
  }
}
```

Příklad přepsání platného signálu (použít platnost `S2_NDVI` pro `PF_NDVI` a zakázat filtrování platnosti pro `S2_EVI`):

```json
{
  "options": {
    "validSignalTypeOverrides": {
      "PF_NDVI": "S2_NDVI",
      "S2_EVI": null
    }
  }
}
```

---

### Konfigurace indikátorů

Pro každý typ indikátoru může konfigurace rozsahu definovat:

**Co ovládá**:

1. **Které typy indikátorů jsou dostupné**: Rozsah určuje, které indikátory jsou vypočítávány
2. **Výchozí viditelnost ve vizualizaci signálů a indikátorů**: Ve výchozím nastavení zobrazeno nebo skryto
3. **Výchozí viditelnost v nápovědě**: Ve výchozím nastavení v nápovědách nebo ne
4. **Barevná schémata**: Barvy pro světlý a tmavý režim (jeden barevný řetězec nebo pole pro gradienty)
5. **Schémata obarvení na základě atributů pro vizualizaci na mapě** (`layerColourScheme`):
   - Definuje mapování hodnot na barvy
   - Příklad: Barvy semaforu (zelená, žlutá, červená)
   - Používá se při obarvování zákresu JŽ podle výsledků indikátorů

**Dopad na uživatele**:

- Dostupné indikátory se liší podle rozsahu
- Možnosti obarvení zákresu JŽ na mapě závisí na konfiguraci indikátorů
- Barvy indikátorů ve vizualizacích odpovídají nastavení rozsahu

**Příklad**:

```json
{
  "markers": {
    "ARPS_HOMOGENEITY_MARKER": {
      "sigMaVisShow": false,
      "sigMaVisTooltip": false,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      },
      "layerColourScheme": [
        {
          "value": "HOMOGENEOUS",
          "colour": "#009688"
        },
        {
          "value": "HETEROGENEOUS",
          "colour": "#FFC107"
        }
      ]
    }
  }
}
```

---

### Zobrazení atributů zákresu JŽ

**Co ovládá**:

Klíč `foiAttributes` ovládá, které dodatečné atributy zákresu JŽ jsou v aplikaci zobrazeny a jak vypadají. Jedná se o záznam klíčovaný kódem typu zákresu JŽ (např. `"PARCEL"`), kde každá hodnota je pole položek pro zobrazení atributů.

Každá položka v poli obsahuje:

| Pole                   | Povinné                           | Popis                                                                                        |
| ---------------------- | --------------------------------- | -------------------------------------------------------------------------------------------- |
| `attributeKey`         | Ano                               | Identifikátor atributu ke konfiguraci                                                        |
| `preview`              | Ne                                | Ovládá viditelnost v panelu **Náhled výběru** na mapě                                        |
| `preview.collapsed`    | Ne                                | `true` pro zobrazení tohoto atributu ve sbaleném (souhrnném) stavu panelu                    |
| `preview.expanded`     | Ne                                | `true` pro zobrazení tohoto atributu při rozbalení panelu                                    |
| `sigMaVis`             | Ne                                | Vykreslí atribut jako statický referenční indikátor v grafu vizualizace signálů a indikátorů |
| `sigMaVis.label`       | Ano (pokud je sigMaVis nastaveno) | Zobrazovaný popisek pro referenční linii                                                     |
| `sigMaVis.show`        | Ano (pokud je sigMaVis nastaveno) | Zda je referenční linie ve výchozím nastavení zobrazena                                      |
| `sigMaVis.tooltip`     | Ano (pokud je sigMaVis nastaveno) | Zda se referenční linie zobrazuje v nápovědách grafu                                         |
| `sigMaVis.color.light` | Ano (pokud je sigMaVis nastaveno) | Hexadecimální barva pro světlý režim                                                         |
| `sigMaVis.color.dark`  | Ano (pokud je sigMaVis nastaveno) | Hexadecimální barva pro tmavý režim                                                          |

**Dopad na uživatele**:

- Atributy nakonfigurované pomocí `preview` se zobrazí v plovoucím panelu **Náhled výběru**, který se otevře po kliknutí na zákres JŽ na mapě (viz [Panel náhledu výběru](./02-getting-started#panel-náhled-výběru)). Příznaky `collapsed` / `expanded` ovládají hustotu informací: klíčové identifikátory viditelné na první pohled vs. celá sada při rozbalení panelu.
- Atributy nakonfigurované pomocí `sigMaVis` se zobrazí jako vodorovné referenční linie v grafu vizualizace signálů a indikátorů spolu s časovými řadami signálů a indikátorů (viz [Modul pohledu zákresu JŽ](./03-foi-view-module)) — užitečné pro zobrazení statické hodnoty (např. deklarovaná výměra, referenční NDVI) přímo v grafu.

**Příklad**:

```json
{
  "foiAttributes": {
    "PARCEL": [
      {
        "attributeKey": "declared_area_ha",
        "preview": {
          "collapsed": true,
          "expanded": true
        },
        "sigMaVis": {
          "label": "Declared area (ha)",
          "show": true,
          "tooltip": true,
          "color": {
            "light": "#007C91",
            "dark": "#00CBE5"
          }
        }
      },
      {
        "attributeKey": "farmer_name",
        "preview": {
          "collapsed": false,
          "expanded": true
        }
      }
    ]
  }
}
```

---

### Nastavení víceosé vizualizace

**Co ovládá**:

1. **Zapnutí/vypnutí víceosých grafů**: Zapnutí nebo vypnutí funkce více os
2. **Konfigurace až 3 os**: Definice až 3 os Y
3. **Vlastnosti osy**:
   - **Pozice**: Levá nebo pravá strana grafu
   - **Doména**: Minimální a maximální hodnoty osy
   - **Popisky**: Volitelné popisky osy
   - **Přiřazení typů signálů**: Které signály jsou vykresleny na které ose

**Dopad na uživatele**:

- Pokud je funkce zapnuta, může **Vizualizace signálů a indikátorů** zobrazit více os
- Signály s různými rozsahy hodnot (např. 0–1 pro NDVI, 0–900 pro CCC) se zobrazí na samostatných osách
- Usnadňuje porovnávání různorodých signálů

**Omezení**:

- Maximálně 3 osy celkem
- Maximálně 2 osy na každé straně (levá nebo pravá)
- ID os musí být jedinečná

**Příklad**:

```json
{
  "sigMaVisAxes": {
    "enabled": true,
    "axes": [
      {
        "id": "land surface temperature",
        "position": "right",
        "domain": [0, 45],
        "tickCount": 10,
        "signalTypeCodes": ["LST_10M_AVG", "LST_10M_CLIM", "LST_10M_V2_LST_C"]
      }
    ]
  }
}
```

---

### Integrace fotografií

**Co ovládá**:

1. **Zapnutí/vypnutí geotagovaných fotografií**: Zapnutí nebo vypnutí zobrazení fotografií
2. **ID zákazníka pro přístup k fotografické službě**: Identifikátor pro přístup k fotografickým datům

**Dopad na uživatele**:

- Pokud je funkce zapnuta, zobrazí se geotagované fotografie na mapě
- Fotografie poskytují vizuální kontext pro pozemní pravdu (ground truth)
- Ne všechny rozsahy mají přístup k fotografiím

**Příklad**:

```json
{
  "photos": {
    "show": true,
    "customerId": "YOUR_CUSTOMER_ID"
  }
}
```

---

### Generování PDF sestav

**Co ovládá**:

- Zda se záložka **Sestavy** zobrazuje ve **Validační konzoli** pro daný rozsah
- Povoluje se nastavením `reporting.pdfGeneration.show` na `true`

**Dopad na uživatele**:

- Po povolení se záložka **Sestavy** zobrazí v levém sloupci **Validační konzole**
- Uživatelé mohou vybrat pozorování a vygenerovat PDF sestavu přímo z **Validační konzole**
- Pokud je funkce zakázána (nebo není nastavena), záložka Sestavy se nezobrazí

Viz [Validační konzole](./04-validation-console#validační-konzole), kde najdete postup pro generování PDF sestavy po povolení této funkce pro váš rozsah.

**Příklad**:

```json
{
  "reporting": {
    "pdfGeneration": {
      "show": true
    }
  }
}
```

## Souhrn dopadu na uživatele

Různé rozsahy mohou mít zcela odlišné:

| Aspekt                            | Příklad dopadu                                                                                                          |
| --------------------------------- | ----------------------------------------------------------------------------------------------------------------------- |
| **Dostupné vrstvy**               | Rozsah A má 15 vrstev; Rozsah B má 5 vrstev                                                                             |
| **Typy signálů a indikátorů**     | Rozsah A sleduje sekání; Rozsah B ne                                                                                    |
| **Chování mapy**                  | Rozsah A používá metrické jednotky; Rozsah B imperiální                                                                 |
| **Výchozí časová období**         | Rozsah A: leden–prosinec; Rozsah B: květen–září                                                                         |
| **Přístup k fotografiím**         | Rozsah A má fotografie; Rozsah B ne                                                                                     |
| **Vzhled zákresu JŽ na mapě**     | Rozsah A barví parcely podle indikátoru homogenity; Rozsah B používá pevnou barvu                                       |
| **PDF sestavy**                   | Rozsah A zobrazuje záložku Sestavy; Rozsah B ne                                                                         |
| **Mapová projekce**               | Rozsah A používá Web Mercator; Rozsah B registruje českou S-JTSK pro národní data                                       |
| **Zobrazení atributů zákresu JŽ** | Rozsah A zobrazuje deklarovanou výměru v panelu Náhled výběru a jako referenční linii v grafu; Rozsah B nezobrazuje nic |

**Klíčový závěr**: Při přepnutí rozsahu se dostupné funkce a data aplikace změní. Vždy zkontrolujte konfiguraci rozsahu, abyste pochopili, co je k dispozici.
